import React, { useEffect, useState } from "react";
import Spacing from "../../Spacing";
import { useDocumentTitle } from "../../../hooks/customDocumentTitleHook";
import { can_visit, capitalizeFirstLetter, getPractice, notify } from "../../../helpers";
import { Loader, Plus } from "lucide-react";
import { getFeatureConfigsData, updateFeatureConfigsData } from "../../../services/users";
import AddFeatureModal from "../../Modal/AddFeatureModal";
import Skeleton from "react-loading-skeleton";

export default function FeatureConfigs({selectedPracticeChange, fetchChangePracticeData}) {
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    account_id = selectedPracticeChange?.account_id;
  }

  const [enableLoader, setEnableLoader] = useState(false);
  const [featureConfigsData, setFeatureConfigsData] = useState([]);
  const [updatedValues, setUpdatedValues] = useState({});
  const [addNewFeature, setAddNewFeature] = useState(false);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  useEffect(() => {
    fetchFeatureConfigData();
  }, [account_id]);

  const fetchFeatureConfigData = async () => {
    setEnableLoader(true);
    try {
      const response = await getFeatureConfigsData();
      if (response) {
        if(response?.data){
          const sortedResponse = response?.data.sort((a, b) => a.name.localeCompare(b.name));
          setFeatureConfigsData(sortedResponse);
          setEnableLoader(false);
        }
      }
    } catch (error) {
      setEnableLoader(false);
      console.error("Error fetching data:", error);
    }
  };

  const handleSwitchChange = (id, checked) => {
    setUpdatedValues((prev) => ({
      ...prev,
      [id]: checked,
    }));
  
    setFeatureConfigsData((prevData) =>
      prevData.map((item) =>
        item.id === id ? { ...item, value: checked } : item
      )
    );
  };

  const handleFeatureConfigUpdate = async (e) => {
    e.preventDefault();

    if(can_visit("administrator") && can_visit("administrator_feature_config")) {
      try {
        if (Object.keys(updatedValues).length === 0) {
          notify("error", "No data change available");
          return;
        }

        setEnableLoader(true);
        e.target.disabled = true;

        const response = await updateFeatureConfigsData({
          account_id: account_id,
          updatedValues: updatedValues,
        });

        if (response.detail) {
          notify('error', response.detail);
        } else { 
          if(response.data.status === 'success'){
            notify("success", response?.data?.message);
            fetchFeatureConfigData();
          } else {
            notify("error", response?.data?.message);
          }
          setUpdatedValues({});
        }

        setEnableLoader(false);
        e.target.disabled = false;
      } catch (error) {
        setEnableLoader(false);
        if (error?.response && error?.response?.data && error?.response?.data?.detail) {
          notify("error", error?.response?.data?.detail, 0.5);
        }
      }
    }
  };

  useDocumentTitle("Feature Configs");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24 docpro_mb_0">
          Feature Configs
        </h2>
        <div className="docpro_page_heading_btns">
          <button
            className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
            onClick={() => {
              setAddNewFeature(true);
              setPreviousScrollPosition(window.scrollY);
              window.scrollTo(0, 0);
            }}
          >
            <Plus width={20} height={20} />
            <span>Add Feature</span>
          </button>
        </div>
      </div>
      <Spacing lg="10" sm="15" />

      <div className="docpro_card">
        {!enableLoader ? (
          <form onSubmit={handleFeatureConfigUpdate} autoComplete="off" className="relative">

            <div className="docpro_table_responsive" style={{ maxHeight: "65vh", overflow: "hidden" }}>
              <table className="docpro_mb_0" style={{  width: "100%" }}>
                <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '48%' }}>Title</th>
                    <th style={{ width: '40%' }}>Identifier Name</th>
                    <th className="docpro_text_center" style={{ width: '12%' }}>Enable</th>
                  </tr>
                </thead>
              </table>
              <div className="docpro_table_responsive custom-scroll" style={{ maxHeight: "62vh", overflowY: "auto" }}>
                <table className="docpro_mb_0" style={{ width: "100%" }}>
                  <tbody>
                    {featureConfigsData.length > 0 ? (
                      featureConfigsData?.map((item, index) => (
                        <tr key={index}>
                          <td style={{ width: '48%' }}>{capitalizeFirstLetter(item?.name)}</td>
                          <td style={{ width: '40%' }}>{item?.name}</td>
                          <td className="docpro_text_center" style={{ width: '12%' }}>
                            {item?.name.toLowerCase().includes('_role_') ? null : (
                              <label className="switch docpro_mb_0 mt-1">
                                <input
                                  type="checkbox"
                                  name="global_schedule"
                                  defaultChecked={item?.value}
                                  onChange={(e) => handleSwitchChange(item?.id, e.target.checked)}
                                />
                                <span className="slider round"></span>
                              </label>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className="no_data" colSpan="3">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <Spacing lg="32" sm="32" />
            <div className="docpro_modal_btns">
              <button
                className={`docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg`}
                style={{ pointerEvents: `${enableLoader ? "none" : "initial"}` }}
                type="submit"
              >
                Save
                {enableLoader && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
            </div>

            {enableLoader && (
              <div className="absolute top-0 left-0 w-full" style={{ height: '100%', zIndex: 3 }}>
                <div className="flex items-center loading" style={{ width: '100%', height: '100%', justifyContent: 'center', background: 'rgb(255 255 255 / 63%)' }}>
                  <span className="docpro_loading" style={{ position: 'inherit' }}>
                    <Loader width={46} height={46} style={{ color: 'var(--accent)' }} />
                  </span>
                </div>
              </div>
            )}
          </form>
        ) : (
          <div className="docpro_table_responsive" style={{ maxHeight: "65vh", overflow: "hidden" }}>
            <table className="docpro_mb_0" style={{  width: "100%" }}>
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                <tr>
                  <th style={{ width: '48%' }}>Title</th>
                  <th style={{ width: '40%' }}>Identifier Name</th>
                  <th className="docpro_text_center" style={{ width: '12%' }}>Enable</th>
                </tr>
              </thead>
            </table>
            <div className="docpro_table_responsive custom-scroll" style={{ maxHeight: "62vh", overflowY: "auto" }}>
              <table className="docpro_mb_0" style={{ width: "100%" }}>
                <tbody>
                  {Array.from({ length: 10 }).map((_, index) => (
                    <tr key={index}>
                      <td style={{ width: "48%" }}>
                        <Skeleton width={`${8 + (index % 2.2)}rem`} borderRadius="8px" />
                      </td>
                      <td style={{ width: "40%" }}>
                        <Skeleton width={`${6 + (index % 4)}rem`} borderRadius="8px" />
                      </td>
                      <td className="docpro_text_center" style={{ width: "12%" }}>
                        <Skeleton width="4rem" borderRadius="8px" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {addNewFeature &&
        <AddFeatureModal
          account_id = {account_id}
          addNewFeature = {addNewFeature}
          setAddNewFeature = {setAddNewFeature}
          autoLoadData = {fetchFeatureConfigData}
          previousScrollPosition={previousScrollPosition}
        />
      }
    </>
  );
}
